import {useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {SnackBarContext} from '../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiWrite} from '../../../../hooks/useApiWrite';
import {useGetRequest} from '../../../../hooks/useGetRequest';
import {Patch} from '../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../lib/api/PATCH/PatchRoutes';
import {getCompanyProfile, getJobPostingById} from '../../../../lib/api/v1';
import {getApplicantMatchById} from '../../../../lib/api/v1/applicant-dashboard';
import {useApplicantCulturePage} from '../../../../pages/applicant/culture/useApplicantCulturePage';
import {SerializeTo} from '../../../../utils/serializer/serialize';
import CompanyProfileSerializer from '../../../company/profile/serializers/CompanyProfileSerializers';
import JobPostingsSerializer from '../../../company/recruiting/job-postings/serializers/JobPostingSerializers';
import {Match} from '../../dashboard/types/ApplicantMatchesTypes';

export const useApplicantMatchesPage = (matchId: string, applicantId: string) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [match, setMatch] = useState<Match>();
  const {dispatchMessage} = useContext(SnackBarContext);
  const {t} = useTranslation();

  const fetchSingleMatch = useMemo(() => {
    return () => getApplicantMatchById(applicantId, matchId);
  }, [applicantId, matchId]);

  const {
    data: remoteMatch,
    loading: isMatchLoading,
    error: matchError,
  } = useGetRequest(fetchSingleMatch, SerializeTo.CamelCase<Match>);

  const {cultureInformation, loading: isCultureInfoLoading, error: cultureInfoError} = useApplicantCulturePage();

  const fetchJobPosting = useMemo(() => {
    return () =>
      getJobPostingById(remoteMatch?.company?.resourceId || '', remoteMatch?.companyJobPosting?.resourceId || '');
  }, [remoteMatch]);

  const {
    data: jobPosting,
    loading: isJobLoading,
    fetchData: fetchJob,
    error: jobPostingError,
  } = useGetRequest(fetchJobPosting, JobPostingsSerializer.toObject, true);

  const fetchProfile = useMemo(() => {
    return () => getCompanyProfile(remoteMatch?.company?.resourceId || '');
  }, [remoteMatch]);

  const {
    data: companyProfile,
    loading: isCompanyProfileLoading,
    fetchData: fetchCompany,
    error: companyError,
  } = useGetRequest(fetchProfile, CompanyProfileSerializer.toObject, true);

  useEffect(() => {
    if (remoteMatch) {
      setMatch(remoteMatch);
      fetchJob();
      fetchCompany();
    }
  }, [remoteMatch, fetchJob, fetchCompany]);

  const onMatchUpdate = (data: Match) => {
    setMatch(data);
    setIsModalOpen(false);
    dispatchMessage(t('shared.tags.statusUpdate'), 'success');
  };

  const {makeRequest: acceptMatch} = useApiWrite<Patch.AcceptMatchByApplicant, Match>({
    method: 'patch',
    path: PatchRoutes[Patch.AcceptMatchByApplicant](matchId, applicantId),
    onSuccess: onMatchUpdate,
  });

  const {
    makeRequest: rejectMatch,
    isLoading: isUpdatingMatch,
    error: rejectMatchError,
  } = useApiWrite<Patch.RejectMatchByApplicant, Match>({
    method: 'patch',
    path: PatchRoutes[Patch.RejectMatchByApplicant](matchId, applicantId),
    onSuccess: onMatchUpdate,
  });
  return {
    jobPosting,
    isJobLoading,
    isCompanyProfileLoading,
    companyProfile,
    match,
    isMatchLoading,
    matchError,
    companyError,
    jobPostingError,
    acceptMatch,
    rejectMatch,
    rejectMatchError,
    isUpdatingMatch,
    isModalOpen,
    setIsModalOpen,
    cultureInformation,
    cultureInfoError,
    isDataLoading: isJobLoading || isMatchLoading || isCompanyProfileLoading || isCultureInfoLoading,
  };
};
